import LiquidGlassCard from "@/components/landing/LiquidGlassCard"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { BreathingGradient } from "@/components/ui/living-brand"
import { Heading, Text } from "@/components/ui/typography"
import { formatWithTooltips } from "@/lib/content/formatWithTooltips"
import { sfIcons } from '@/lib/sf-icons'
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

// Systematic icon mapping for features (P1 Landing Blocks)
// Uses semantic icons from sf-icons.ts for consistency
const featureIconMap: Record<string, typeof sfIcons[keyof typeof sfIcons]> = {
  "zap": sfIcons.zap,              // automation - lightning fast
  "clock": sfIcons.speed,          // speed - 15-minute settlement
  "globe": sfIcons.coverageCountries, // global - 180+ countries
  "shield": sfIcons.kycAml,        // compliance - KYC/AML built-in
  "layers": sfIcons.coverageNetworks, // multichain - blockchain networks
  "dollar-sign": sfIcons.fiat,     // rates - transparent fees
  "lock": sfIcons.zeroTrust,       // security - zero-trust architecture
  "activity": sfIcons.activity,     // livingSystem - always online
} as const

type FeaturesSectionProps = {
  id?: string
}

export function FeaturesSection({ id }: FeaturesSectionProps) {
  const { t } = useTranslation("marketing")

  // Features from SSOT (marketing.json) - using actual keys
  const featureKeys = ["automation", "speed", "global", "compliance"] as const
  type FeatureKey = typeof featureKeys[number]
  const featureList = featureKeys.map((key: FeatureKey) => {
    const iconName = t(`features.${key}.icon`) as string
    return {
      id: key,
      title: t(`features.${key}.title`),
      description: t(`features.${key}.description`),
      details: [] as string[], // features don't have details array in SSOT
      icon: featureIconMap[iconName] || sfIcons.info, // Fallback to info icon
    }
  })

  return (
    <section
      id={id}
      className={cn(
        "relative py-12 xs:py-14 sm:py-24 overflow-hidden",
        "outline-none", // Remove any focus outline (section is not interactive)
        // Subtle gradient background for glass visibility (dark mode aware)
        "bg-gradient-to-b from-slate-50 via-white to-slate-50/80",
        "dark:from-slate-950 dark:via-slate-900 dark:to-slate-950/80"
      )}
      aria-labelledby="landing-features"
      tabIndex={-1} // Prevent focus outline (section is not interactive)
    >
      {/* Color Lab: Bold Blue-Purple Mesh gradient */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 20% 20%, hsl(220 65% 50% / 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, hsl(270 60% 72% / 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 50%, hsl(210 65% 48% / 0.08) 0%, transparent 60%)
          `
        }}
      />
      {/* Living Brand: Breathing Gradient (subtle, syncs with LED heartbeat) */}
      <BreathingGradient
        variant="blue"
        syncWithLed
        className="absolute inset-0 z-[1] pointer-events-none opacity-20"
      />
      {/* Content container with z-index to ensure cards are above gradients */}
      <div className="relative z-10 mx-auto max-w-6xl space-y-6 xs:space-y-7 sm:space-y-10 md:space-y-12 px-4 xs:px-4 sm:px-6">
        <div className="space-y-4 sm:space-y-5 md:space-y-6 text-center">
          <div className="space-y-3 sm:space-y-4 md:space-y-5">
            <Heading level={2} id="landing-features">
              {t("featuresSection.headline")}
            </Heading>
            <Text variant="lg" color="muted" className="mx-auto max-w-2xl leading-relaxed">
              {t("featuresSection.description")}
            </Text>
            <div className="flex items-center justify-center gap-2 pt-2">
              {((t("featuresSection.badges", { returnObjects: true }) as string[]) || []).map((badge, idx) => (
                <React.Fragment key={badge}>
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-xs",
                      semanticTokens.border.default,
                      semanticTokens.fg.muted
                    )}
                  >
                    {badge}
                  </Badge>
                  {idx < ((t("featuresSection.badges", { returnObjects: true }) as string[]) || []).length - 1 && (
                    <Text as="span" variant="caption" color="subtle" aria-hidden="true">•</Text>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xs:grid-cols-1 gap-4 xs:gap-4 sm:gap-5 md:gap-6 md:grid-cols-2">
          {featureList.map((feature) => {
            const Icon = feature.icon
            if (!Icon) return null
            return (
              <LiquidGlassCard
                key={feature.id}
                tier="surface"
                interactive
                className="flex flex-col"
              >
                <div className={cn("p-6 sm:p-7 md:p-8")}>
                  <div className="space-y-4 sm:space-y-5 md:space-y-6">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className={cn(
                        "inline-flex items-center justify-center rounded-xl transition-colors",
                        "border-2",
                        "h-10 w-10 sm:h-11 sm:w-11 md:h-12 md:w-12",
                        semanticTokens.border.default,
                        semanticTokens.accent.fg, // Use accent color for icons
                        "group-hover:border-primary/80 group-hover:text-primary"
                      )}>
                        <Icon className="h-5 w-5 sm:h-5.5 sm:w-5.5 md:h-6 md:w-6" aria-hidden="true" />
                      </div>
                      <Heading level={3}>
                        {feature.title}
                      </Heading>
                    </div>

                    <Text variant="sm" color="muted" className="leading-relaxed">
                      {formatWithTooltips(feature.description)}
                    </Text>

                    <ul className={cn(
                      "space-y-2 sm:space-y-3 pt-3 sm:pt-4 border-t",
                      semanticTokens.border.subtle
                    )}>
                      {feature.details.map((detail) => (
                        <li key={detail} className="flex items-start gap-2 sm:gap-3">
                          <span aria-hidden="true" className={cn(
                            "mt-1.5 h-1 w-1 sm:h-1.5 sm:w-1.5 rounded-full flex-shrink-0",
                            semanticTokens.accent.primary
                          )} />
                          <Text variant="caption" color="muted" as="span">
                            {formatWithTooltips(detail)}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </LiquidGlassCard>
            )
          })}
        </div>

        <div className="text-center mt-8 sm:mt-10">
          <Button
            asChild
            variant="outline"
            className={cn(
              "gap-2",
              "min-h-[44px] touch-manipulation",
              semanticTokens.focus.ring
            )}
          >
            <Link to="/coverage">
              {t("cta.learnMore")}
              <sfIcons.next className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  )
}
